import React from 'react';
import RegionsComponent from '../components/Regions';
import { Button } from '../components/ui/button';
import { ArrowRight, Globe, MapPin, Users, Shield, TrendingUp, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Regions = () => {
  const navigate = useNavigate();

  return (
    <div className="regions-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 mb-16">
            <div className="inline-block">
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold flex items-center gap-2 mx-auto w-fit">
                <Globe className="w-4 h-4" />
                Global Coverage
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              47 Jurisdictions Across
              <br />
              <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                EMEA & APAC Markets
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Comprehensive governance intelligence powered by 70-year established networks 
              with government officials, central banks, and business leaders across target markets.
            </p>
          </div>

          {/* Global Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              { icon: MapPin, value: "47", label: "Jurisdictions", color: "blue" },
              { icon: Users, value: "70+", label: "Years Networks", color: "green" },
              { icon: Globe, value: "2", label: "Major Regions", color: "indigo" },
              { icon: Shield, value: "90%+", label: "Accuracy Target", color: "purple" }
            ].map((stat, index) => (
              <div key={index} className={`bg-gradient-to-br from-${stat.color}-50 to-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
                <stat.icon className={`w-10 h-10 text-${stat.color}-600 mb-3`} />
                <p className="text-4xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-600 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Regions Component */}
      <RegionsComponent />

      {/* Regional Expertise Details */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* EMEA Deep Dive */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Globe className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-4xl font-bold text-gray-900">EMEA Expertise</h2>
                <p className="text-lg text-gray-600">
                  32 jurisdictions across Europe, Middle East & Africa with deep regulatory and cultural expertise
                </p>
              </div>

              <div className="relative h-64 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=800&q=80"
                  alt="European business"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 to-transparent flex items-end p-6">
                  <p className="text-white font-semibold text-lg">European Financial Hubs</p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Key Capabilities</h3>
                <div className="space-y-3">
                  {[
                    {
                      title: "EU Regulatory Framework",
                      desc: "GDPR compliance, financial regulations, data protection"
                    },
                    {
                      title: "Central Banking Networks",
                      desc: "Direct access through 25+ year central bank leadership"
                    },
                    {
                      title: "Government Relations",
                      desc: "Established connections with policy makers and officials"
                    },
                    {
                      title: "Cross-Border Trade",
                      desc: "Navigate complex customs, tax, and regulatory requirements"
                    },
                    {
                      title: "Cultural Intelligence",
                      desc: "Multi-lingual expertise across diverse European markets"
                    },
                    {
                      title: "Anti-Corruption",
                      desc: "G20 toolkit expertise and ethical business practices"
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                      <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">{item.title}</p>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1590650153855-d9e808231d41?w=400&q=80"
                  alt="Middle East business"
                  className="rounded-xl shadow-lg h-40 w-full object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1564760055775-d63b17a55c44?w=400&q=80"
                  alt="African markets"
                  className="rounded-xl shadow-lg h-40 w-full object-cover"
                />
              </div>
            </div>

            {/* APAC Deep Dive */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-4xl font-bold text-gray-900">APAC Expertise</h2>
                <p className="text-lg text-gray-600">
                  15 jurisdictions across Asia-Pacific with proven cultural intelligence and local partnerships
                </p>
              </div>

              <div className="relative h-64 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1555883006-0f5a0915a80f?w=800&q=80"
                  alt="Asian business district"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-900/70 to-transparent flex items-end p-6">
                  <p className="text-white font-semibold text-lg">Asia-Pacific Growth Markets</p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Key Capabilities</h3>
                <div className="space-y-3">
                  {[
                    {
                      title: "Cultural Navigation",
                      desc: "Deep understanding of hierarchical and relationship-based markets"
                    },
                    {
                      title: "Regulatory Complexity",
                      desc: "Navigate diverse legal frameworks and business practices"
                    },
                    {
                      title: "Local Partnerships",
                      desc: "Established networks with business leaders and officials"
                    },
                    {
                      title: "Market Entry Strategy",
                      desc: "Proven frameworks for successful APAC expansion"
                    },
                    {
                      title: "Language Expertise",
                      desc: "Multi-lingual capabilities across major APAC markets"
                    },
                    {
                      title: "Risk Mitigation",
                      desc: "Cultural due diligence and compliance frameworks"
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">{item.title}</p>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1536098561742-ca998e48cbcc?w=400&q=80"
                  alt="Southeast Asia"
                  className="rounded-xl shadow-lg h-40 w-full object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1493515322954-4fa727e97985?w=400&q=80"
                  alt="East Asia markets"
                  className="rounded-xl shadow-lg h-40 w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories by Region */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Regional Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from enterprises leveraging our regional expertise
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                region: "EMEA",
                company: "Infrastructure Group",
                result: "8 markets, zero compliance issues",
                image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&q=80",
                stats: ["6 months faster", "90% accuracy", "Full compliance"]
              },
              {
                region: "APAC",
                company: "Financial Services",
                result: "18 → 6 months expansion timeline",
                image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80",
                stats: ["12 months saved", "90%+ success rate", "Risk mitigation"]
              },
              {
                region: "Cross-Regional",
                company: "Manufacturing Corp",
                result: "Systematic advantage across 15 markets",
                image: "https://images.unsplash.com/photo-1565515636369-61fa6c6c7926?w=400&q=80",
                stats: ["15 jurisdictions", "Cultural intelligence", "Network access"]
              }
            ].map((story, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={story.image} 
                    alt={story.company}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 space-y-4">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                    {story.region}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900">{story.company}</h3>
                  <p className="text-gray-600 font-semibold">{story.result}</p>
                  <div className="space-y-2 pt-4 border-t border-gray-200">
                    {story.stats.map((stat, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        {stat}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-green-600">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Expand With Confidence
          </h2>
          <p className="text-xl text-blue-100">
            Leverage our 70-year networks and cultural expertise across 47 jurisdictions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg transition-all duration-200 hover:shadow-xl group"
            >
              Discuss Your Market
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/platform')}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg transition-all duration-200"
            >
              Explore Platform
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Regions;


